import { DestroyRef, Injectable, signal, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormControlStatus } from '@angular/forms';
import { Observable, combineLatest, distinctUntilChanged, map, startWith } from 'rxjs';

/**
 * @description Form's value, enabled, pristine, and valid.
 *
 * Does not include redundant stuff like `invalid`, since this tracks `valid`.
 * Does not track `touched` since it is very tricky
 */
export class FormPropsSignal<T> {
    value: T;
    enabled: boolean;
    status: 'VALID' | 'INVALID' | 'PENDING' | 'DISABLED';
    pristine: boolean;
    valid: boolean;

    constructor(form: AbstractControl<unknown>) {
        this.value = form?.getRawValue();
        this.enabled = form?.enabled;
        this.status = form?.status;
        this.pristine = form?.enabled;
        this.valid = form?.enabled;
    }
}
@Injectable({
    providedIn: 'root',
})
export class FormToSignalsService {
    readonly #fb = inject(FormBuilder);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    #formSignal = signal<FormPropsSignal<any>>(new FormPropsSignal<any>(this.#fb.control(null)));
    public formSignal = this.#formSignal.asReadonly();

    /**
     * @description Gives relevant form properties as a Signal. @see {@link FormPropsSignal}
     * @description WARNING!
     *                  Subscribes to `statusChanges`. Manually doing stuff like `markAsDirty` will not trigger that.
     *                  You would need to then call `updateValueAndValidity()`. This is also why those properties are not included.
     * @param form Any type of form element, including arrays and groups and controls
     * @param signal The signal which will be set
     * @param destroyRef Required for subscription to be destroyed
     */
    formPropsToSignal(form: AbstractControl<unknown>, destroyRef: DestroyRef): void {
        this.#formSignal.set({
            value: form.getRawValue(),
            enabled: form.enabled,
            status: form.status,
            pristine: form.pristine,
            valid: form.valid,
        });
        form.statusChanges.pipe(takeUntilDestroyed(destroyRef)).subscribe(() => {
            this.#formSignal.set({
                value: form.getRawValue(),
                enabled: form.enabled,
                status: form.status,
                pristine: form.pristine,
                valid: form.valid,
            });
        });
    }
}

/**
 * @description Separate from the whole Form To Signal util class
 * @param form Control or group or array
 * @returns Raw form data observable, typed
 */
export function formValues<T>(form: AbstractControl<T>): Observable<T> {
    return form.valueChanges.pipe(
        startWith(form.value),
        map(value => value)
    );
}
/**
 * @description Separate from the whole Form To Signal util class
 * @param form Control or group or array
 * @returns Raw form data observable, typing inferred
 */
export function formValuesInferred<T>(form: AbstractControl<T>) {
    return form.valueChanges.pipe(map(value => value));
}

export function formValuesRaw<T>(form: AbstractControl<T>) {
    return form.valueChanges.pipe(
        distinctUntilChanged((pre: T, curr: T) => JSON.stringify(pre) === JSON.stringify(curr)),
        map(() => form.getRawValue())
    );
}

/**
 * @description Separate from the whole Form To Signal util class
 * @param form Control or group or array
 * @returns Status data observable
 */
export function formStatus<T>(form: AbstractControl<T>): Observable<FormControlStatus> {
    return form.statusChanges.pipe(map(() => form.status));
}

/**
 * @description Separate from the whole Form To Signal util class
 * @param form Control or group or array
 * @returns Raw form data and status observable, typed
 */
export function formCombined<T>(form: AbstractControl<T>) {
    return combineLatest([formValuesRaw(form), formStatus(form)]).pipe(
        map(([val, status]) => {
            return {
                value: val,
                status: status,
            };
        })
    );
}

/**
 * @description Separate from the whole Form To Signal util class
 * @param form Control or group or array
 * @returns Raw form data and status observable, typing inferred
 */
export function formCombinedInferred<T>(form: AbstractControl<T>) {
    return combineLatest([formValues(form), formStatus(form)]).pipe(
        map(([val, status]) => {
            return {
                value: val,
                status: status,
            };
        })
    );
}
